import React, { useContext, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiArrowLeft,
  FiFileText,
  FiShield,
  FiMail,
  FiChevronRight,
  FiExternalLink,
} from 'react-icons/fi';
import { ThemeContext } from '../context/ThemeContext';
import MarketingLayout from '../layouts/MarketingLayout';
import { SUPPORT_EMAIL } from '../config/emailjs';
import { scrollMainToId, useMainScrollSpy } from '../utils/mainScroll';

function sectionAnchor(section, index) {
  if (section.id) return section.id;
  const slug = section.heading
    .replace(/^\d+\.\s*/, '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || `seccion-${index + 1}`;
}

function estimateReadingMinutes(sections) {
  const text = sections
    .flatMap((s) => [...(s.paragraphs || []), ...(s.bullets || [])])
    .join(' ');
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(2, Math.ceil(words / 180));
}

export default function LegalDocument({
  title,
  updated,
  sections,
  variant = 'terms',
  intro,
}) {
  const { modoOscuro } = useContext(ThemeContext);
  const location = useLocation();

  const pageBg = modoOscuro ? 'bg-black' : 'bg-[#f5f5f7]';
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const chipBg = modoOscuro ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200';
  const tocActive = modoOscuro
    ? 'bg-[#1F80FF]/15 text-[#5CA3FF] border-[#1F80FF]/40'
    : 'bg-[#1F80FF]/10 text-[#004AAD] border-[#1F80FF]/30';
  const tocIdle = modoOscuro
    ? 'border-transparent text-gray-400 hover:text-white hover:bg-gray-800/60'
    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100';

  const anchors = useMemo(
    () => sections.map((s, i) => ({ ...s, anchor: sectionAnchor(s, i) })),
    [sections]
  );

  const sectionIds = useMemo(() => anchors.map((a) => a.anchor), [anchors]);
  const activeAnchor = useMainScrollSpy(sectionIds);
  const readingMin = useMemo(() => estimateReadingMinutes(sections), [sections]);

  const isPrivacy = variant === 'privacy';
  const HeroIcon = isPrivacy ? FiShield : FiFileText;
  const kicker = isPrivacy ? 'PRIVACIDAD Y DATOS' : 'TÉRMINOS DE USO';
  const related = isPrivacy
    ? { to: '/terminos', label: 'Términos de uso' }
    : { to: '/privacidad', label: 'Política de privacidad' };

  useEffect(() => {
    const hash = location.hash?.replace('#', '').trim();
    if (!hash || !sectionIds.includes(hash)) return undefined;

    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => scrollMainToId(hash));
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [location.pathname, location.hash, sectionIds]);

  const scrollToSection = (anchor) => {
    scrollMainToId(anchor);
  };

  return (
    <MarketingLayout>
      <div className={pageBg}>
        <section className="pt-10 pb-8 sm:pt-14 sm:pb-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className={`inline-flex items-center gap-2 text-sm mb-8 ${textMuted} hover:text-[#1F80FF] transition-colors`}
            >
              <FiArrowLeft className="w-4 h-4" />
              Volver al inicio
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                  modoOscuro ? 'bg-[#1F80FF]/15 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                }`}
              >
                <HeroIcon className="w-7 h-7" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-[11px] font-semibold tracking-[0.2em] uppercase mb-2 ${textMuted}`}>
                  {kicker}
                </p>
                <h1 className={`text-3xl sm:text-4xl font-semibold leading-tight mb-3 ${textPri}`}>{title}</h1>
                {intro && (
                  <p className={`text-sm sm:text-base leading-relaxed max-w-2xl ${textSec}`}>{intro}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-5">
                  {updated && (
                    <span className={`text-[11px] font-medium px-3 py-1 rounded-full border ${chipBg} ${textSec}`}>
                      Actualizado: {updated}
                    </span>
                  )}
                  <span className={`text-[11px] font-medium px-3 py-1 rounded-full border ${chipBg} ${textSec}`}>
                    Lectura ~{readingMin} min
                  </span>
                  <span className={`text-[11px] font-medium px-3 py-1 rounded-full border ${chipBg} ${textSec}`}>
                    Idioma: español (AR)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-14 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:hidden mb-6 overflow-x-auto overscroll-x-contain">
              <div className="flex gap-2 pb-1 w-max max-w-full pr-2">
                {anchors.map(({ anchor, heading }) => (
                  <button
                    key={anchor}
                    type="button"
                    onClick={() => scrollToSection(anchor)}
                    className={`whitespace-nowrap text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      activeAnchor === anchor ? tocActive : tocIdle
                    }`}
                  >
                    {heading.replace(/^\d+\.\s*/, '')}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-8 lg:gap-10 items-start">
              <div className="space-y-4 min-w-0">
                {anchors.map(({ anchor, heading, paragraphs = [], bullets = [] }) => (
                  <article
                    key={anchor}
                    id={anchor}
                    className={`scroll-mt-24 border rounded-2xl p-5 sm:p-6 ${box}`}
                  >
                    <h2 className={`text-base sm:text-lg font-semibold mb-3 ${textPri}`}>{heading}</h2>
                    {paragraphs.map((p, i) => (
                      <p key={i} className={`text-sm leading-relaxed mb-3 last:mb-0 ${textSec}`}>
                        {p}
                      </p>
                    ))}
                    {bullets.length > 0 && (
                      <ul className={`mt-2 space-y-2 text-sm leading-relaxed list-disc pl-5 ${textSec}`}>
                        {bullets.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}

                <p className={`text-xs leading-relaxed pt-2 ${textMuted}`}>
                  Este texto es informativo y no reemplaza asesoramiento legal. Para obligaciones fiscales o formales,
                  consultá a un profesional matriculado.
                </p>
              </div>

              <aside className="space-y-4 lg:sticky lg:top-24 self-start">
                <nav
                  className={`hidden lg:block rounded-2xl border p-4 ${box}`}
                  aria-label="Índice del documento"
                >
                  <p className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${textMuted}`}>
                    En esta página
                  </p>
                  <ul className="space-y-1">
                    {anchors.map(({ anchor, heading }) => (
                      <li key={anchor}>
                        <button
                          type="button"
                          onClick={() => scrollToSection(anchor)}
                          className={`w-full text-left text-xs sm:text-sm px-2.5 py-2 rounded-lg border transition-colors ${
                            activeAnchor === anchor ? tocActive : tocIdle
                          }`}
                        >
                          {heading}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className={`rounded-2xl border p-4 space-y-3 ${box}`}>
                  <p className={`text-[11px] font-semibold uppercase tracking-wider ${textMuted}`}>
                    Documentos relacionados
                  </p>
                  <Link
                    to={related.to}
                    className={`flex items-center justify-between gap-2 text-sm font-medium rounded-xl px-3 py-2.5 border transition-colors ${
                      modoOscuro
                        ? 'border-gray-700 hover:bg-gray-800 text-[#5CA3FF]'
                        : 'border-gray-200 hover:bg-gray-50 text-[#1F80FF]'
                    }`}
                  >
                    {related.label}
                    <FiChevronRight className="w-4 h-4 shrink-0 opacity-70" />
                  </Link>
                  <Link
                    to="/planes"
                    className={`flex items-center justify-between gap-2 text-sm rounded-xl px-3 py-2.5 border transition-colors ${textSec} ${
                      modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Planes y límites
                    <FiExternalLink className="w-3.5 h-3.5 shrink-0 opacity-60" />
                  </Link>
                  <Link
                    to="/ayuda"
                    className={`flex items-center justify-between gap-2 text-sm rounded-xl px-3 py-2.5 border transition-colors ${textSec} ${
                      modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    Centro de ayuda
                    <FiChevronRight className="w-4 h-4 shrink-0 opacity-60" />
                  </Link>
                </div>

                <div className={`rounded-2xl border p-4 space-y-3 ${box}`}>
                  <p className={`text-sm font-semibold ${textPri}`}>¿Algo no te cierra?</p>
                  <p className={`text-xs leading-relaxed ${textSec}`}>
                    Escribinos y te respondemos con lenguaje claro, sin letra chica innecesaria.
                  </p>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(title)}`}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#1F80FF] hover:underline break-all"
                  >
                    <FiMail className="w-4 h-4 shrink-0" />
                    {SUPPORT_EMAIL}
                  </a>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openContactModal'))}
                    className="w-full text-center px-3 py-2 rounded-xl text-xs font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] transition-colors"
                  >
                    Abrir contacto
                  </button>
                </div>
              </aside>
            </div>

            <div className={`lg:hidden mt-8 rounded-2xl border p-5 space-y-3 ${box}`}>
              <p className={`text-sm font-semibold ${textPri}`}>Documentos y ayuda</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Link
                  to={related.to}
                  className="flex-1 text-center text-sm font-medium px-3 py-2.5 rounded-xl border border-[#1F80FF]/30 text-[#1F80FF] hover:bg-[#1F80FF]/5"
                >
                  {related.label}
                </Link>
                <Link
                  to="/ayuda"
                  className={`flex-1 text-center text-sm font-medium px-3 py-2.5 rounded-xl border ${modoOscuro ? 'border-gray-700' : 'border-gray-200'} ${textSec}`}
                >
                  Ayuda
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}
